import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { getMarketingUser, getPP, getPPByUser, getTC, getTCByUser, putAcceptMarketing, putAcceptTC } from 'api'
import { errorObj } from 'utils/handleError'
import { showErrorMessage } from 'utils/showMessage'
import useAuth from './useAuth'

const useTCPP = (isWorkspace = false, fetchMarketing = false) => {
  const { t } = useTranslation()
  const { platformData, getAccessToken, handleAcceptTC } = useAuth()

  /* ------------------------------------------------------------------ */
  /* STATE                                                              */
  /* ------------------------------------------------------------------ */
  const [tcModal, setTcModal] = useState({
    show: false, tc: null, pp: null, marketing: null          // ← qui terremo il consenso marketing
  })

  /* ------------------------------------------------------------------ */
  /* FETCH T&C                                                          */
  /* ------------------------------------------------------------------ */
  const { isFetching: isTCFetching } = useQuery({
    queryKey: ['getTC'],
    queryFn: () => isWorkspace ? getTCByUser(getAccessToken()) : getTC(getAccessToken()),
    onSuccess: (res) => {
      const list = res.resData.data?.terms_and_conditions ?? res.resData.data?.data ?? []
      if (list.length > 0) {
        setTcModal((prev) => ({
          ...prev, tc: { ...res.resData.data, data: list }
        }))
      }
    },
    onError: (err) => showErrorMessage(errorObj(err)?.resData?.errors?.[0]?.message || t('error')),
    enabled: isWorkspace ? !platformData?.profile?.accepted_term_condition : true,
    refetchOnWindowFocus: false
  })

  /* ------------------------------------------------------------------ */
  /* FETCH Privacy-Policy                                               */
  /* ------------------------------------------------------------------ */
  const { isFetching: isPPFetching } = useQuery({
    queryKey: ['getPP'],
    queryFn: () => isWorkspace ? getPPByUser(getAccessToken()) : getPP(getAccessToken()),
    onSuccess: (res) => {
      const list = res.resData.data?.privacy_policies ?? res.resData.data?.data ?? []
      if (list.length > 0) {
        setTcModal((prev) => ({
          ...prev, pp: { ...res.resData.data, data: list }
        }))
      }
    },
    onError: (err) => showErrorMessage(errorObj(err)?.resData?.errors?.[0]?.message || t('error')),
    enabled: isWorkspace ? !platformData?.profile?.accepted_term_condition : true,
    refetchOnWindowFocus: false
  })

  /* ------------------------------------------------------------------
 * FETCH Marketing-consent
 * ------------------------------------------------------------------ */
  const { isFetching: isMarketingFetching } = useQuery({
    queryKey: ['getMarketingUser'],
    queryFn: () => getMarketingUser(getAccessToken()),
    onSuccess: (res) => {
      setTcModal((prev) => ({
        ...prev,
        marketing: res.resData.data            // { accepted, accepted_at, … }
      }));
    },
    onError: (err) =>
      showErrorMessage(errorObj(err)?.resData?.errors?.[0]?.message || t('error')),
    enabled: fetchMarketing,                  // <<< adesso controllato da flag
    refetchOnWindowFocus: false
  });

  /* ------------------------------------------------------------------ */
  /* DECIDI SE MOSTRARE IL MODALE                                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const tcEmpty = !(tcModal.tc?.data?.length > 0)
    const ppEmpty = !(tcModal.pp?.data?.length > 0)
    // mostralo in workspace se serve accettare almeno uno dei due testi
    setTcModal((prev) => ({
      ...prev, show: isWorkspace && !(tcEmpty && ppEmpty)
    }))
  }, [tcModal.tc, tcModal.pp, isWorkspace])

  /* ------------------------------------------------------------------ */
  /* MUTAZIONI                                                          */
  /* ------------------------------------------------------------------ */
  const acceptTC = useMutation({
    mutationKey: 'putAcceptTC', mutationFn: () => putAcceptTC(getAccessToken()), onSuccess: () => {
      handleAcceptTC()
      closeTcModal()
    }, onError: (err) => showErrorMessage(errorObj(err)?.resData?.errors?.[0]?.message || t('error'))
  })

  const marketingConsent = useMutation({
    mutationKey: 'putAcceptMarketing',
    mutationFn: (accepted) => putAcceptMarketing(getAccessToken(), accepted),
    onSuccess: (_, accepted) => {
      // aggiorna immediatamente lo stato locale: la checkbox riflette il cambiamento
      setTcModal((prev) => ({
        ...prev, marketing: { ...(prev.marketing ?? {}), accepted }
      }))
    },
    onError: (err) => showErrorMessage(errorObj(err)?.resData?.errors?.[0]?.message || t('error'))
  })

  /* ------------------------------------------------------------------ */
  /* HELPERS                                                            */

  /* ------------------------------------------------------------------ */
  function handleSubmitTcModal() {
    acceptTC.mutate()
  }

  function openTcModal() {
    setTcModal((prev) => ({ ...prev, show: true }))
  }

  function closeTcModal() {
    setTcModal((prev) => ({ ...prev, show: false }))
  }

  return {
    /* per la modale */
    tcModal,
    isTCFetching,
    isPPFetching,
    isMarketingFetching,
    handleSubmitTcModal,
    acceptTC,

    /* marketing checkbox */
    initialMarketingAccepted: tcModal.marketing?.accepted ?? false,
    onToggleMarketing: (accepted) => marketingConsent.mutateAsync(accepted),

    /* controlli esterni */
    openTcModal,
    closeTcModal
  }
}

export default useTCPP
